// src/components/Sidebar.jsx
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Bell,
  Search as SearchIcon,
  Webhook,
  ChevronLeft,
  ChevronRight,
  Eye,
  Newspaper,
  Building,
  User,
  Sparkles,
  WandSparkles,
  Headset,
  LogOut,
  LifeBuoy,
  BookOpen,
  CircleGauge,
  Save,
  ShieldCheck,
  Lock,
  Megaphone,
} from "lucide-react";
import { openWhatsNew } from "../utils/whatsNew";
import whitelogo from "../assets/whiteintel-white-logo-new.png";
import blacklogo from "../assets/whiteintel-black-logo-new.png";
import { useBrandProtectionAccess, useInvestigationsAccess, useWebhookAccess } from "../utils/brandProtection";

const NAV_ITEMS = [
    { key: "globalstats",   label: "Global Stats",    icon: CircleGauge,             path: "/global-stats" },

  { key: "search",      label: "Global Search", icon: SearchIcon,     path: "/search" },
  { key: "dashboard",   label: "Global Dashboard",     icon: LayoutDashboard, path: "/dashboard" },

  { key: "events",      label: "Watchlist Events",        icon: Bell,            path: "/watchlist-events" },
  { key: "threatfeed",  label: "Threat Feeds",  icon: Newspaper,       path: "/threat-feeds" },
  { key: "brand",       label: "Brand Protection", icon: ShieldCheck,    path: "/brand-protection" },
  { key: "investigations",      label: "Investigation+",        icon: Sparkles,            path: "/investigations" },
  { key: "savedincidents", label: "Saved Incidents", icon: Save,      path: "/saved-incidents" },
  { key: "watchlist",   label: "Watchlists",    icon: Eye,             path: "/watchlists" },
  { key: "profile",     label: "Profile",       icon: User,            path: "/profile" },
  { key: "organization",label: "Organization",  icon: Building,        path: "/organization-settings" },
  { key: "webhook",     label: "Webhooks",      icon: Webhook,         path: "/webhooks" },
  { key: "upgrades",    label: "Upgrades",      icon: WandSparkles,        path: "/upgrades" },
  { key: "contact",     label: "Contact",       icon: Headset,         path: "/contact" },
];

const NAV_BOTTOM = [
  {
    key: "help",
    label: "Help",
    icon: LifeBuoy,
    href: "https://knowledge.whiteintel.io",
    external: true,
  },
  {
    key: "docs",
    label: "API Docs",
    icon: BookOpen,
    href: "https://docs.whiteintel.io",
    external: true,
  },
];

export default function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,     // kept in signature for compatibility (unused)
  setMobileOpen,  // kept in signature for compatibility (unused)
  activeKey,
  onChange,
}) {
  const navigate = useNavigate();

  // License gates for premium features. We always render the entry (so
  // non-eligible users see the feature exists and can upgrade), but mark it
  // as locked with a Pro pill when their plan doesn't include it. Clicks
  // still navigate to the gated page, which renders an upgrade screen.
  const brandAccess = useBrandProtectionAccess();
  const investigationsAccess = useInvestigationsAccess();
  const webhookAccess = useWebhookAccess();
  const isBrandLocked = brandAccess.available === false;
  const isInvestigationsLocked = investigationsAccess.available === false;
  const isWebhookLocked = webhookAccess.available === false;

  // (Initial collapsed state — including mobile defaults — is handled
  // upstream by the `useSidebarCollapsed` hook each page uses. No mount-time
  // override here, otherwise we'd undo the user's stored preference.)

  const widthClass = collapsed ? "w-14" : "w-56";

  const baseItemClass =
    "group relative mb-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500";

  const activeClasses = "bg-sky-900/40 text-sky-400";
  const inactiveClasses = "text-neutral-300 hover:bg-sky-900/30 hover:text-sky-400";

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("token");
    sessionStorage.removeItem("jwtToken");
    navigate("/login");
  };

  return (
    <>
      {/* Sidebar (always visible; no mobile drawer/overlay) */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-[#0a1224] border-r border-neutral-800 overflow-hidden transition-all ${widthClass}`}
      >
        {/* Top — brand + inline collapse toggle. Collapse pinned here (not
            at the bottom) so it stays visible regardless of how many nav
            items are listed. */}
        <div className="flex items-center justify-between px-3 py-4 gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img
              src={whitelogo}
              alt="WhiteIntel"
              className="h-7 w-7 shrink-0 object-contain"
              loading="eager"
              fetchPriority="high"
            />
            {!collapsed && (
              <span className="text-lg font-extrabold tracking-tight truncate">Whiteintel</span>
            )}
          </Link>
          {!collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-white/[0.06] hover:text-sky-300 transition-colors"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>
        {collapsed && (
          <div className="flex justify-center px-2 -mt-2 pb-2">
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-white/[0.06] hover:text-sky-300 transition-colors"
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Nav — flex-1 + overflow so on short viewports the bottom section
            (Logout, What's New, etc.) stays pinned and the nav scrolls
            internally instead of clipping. Thin custom scrollbar so the
            internal scroll doesn't look like a stock OS one. */}
<nav
  className="flex-1 overflow-y-auto px-2 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(56,189,248,0.25)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-sky-500/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-sky-500/40"
  role="navigation"
>
  {NAV_ITEMS.map(({ key, label, icon: Icon, path }) => {
    const locked =
      (key === "brand" && isBrandLocked) ||
      (key === "investigations" && isInvestigationsLocked) ||
      (key === "webhook" && isWebhookLocked);
    return (
    <NavLink
      key={key}
      to={path}
      end={key !== "search"}
      title={locked ? "Available on Enterprise & Threat Intelligence plans" : undefined}
      className={({ isActive }) =>
        `${baseItemClass} ${
          locked
            ? "text-neutral-500 hover:bg-sky-900/20 hover:text-sky-300"
            : isActive || key === activeKey
            ? activeClasses
            : inactiveClasses
        }`
      }
      onClick={() => {
        onChange?.(key);
        setMobileOpen?.(false);
      }}
    >
      <Icon
        className={`h-5 w-5 shrink-0 transition-colors duration-200 ${
          locked
            ? "text-neutral-500 group-hover:text-sky-400"
            : key === activeKey
            ? "text-sky-400"
            : "group-hover:text-sky-400"
        }`}
      />
      {!collapsed && (
        <div className="flex flex-1 items-baseline justify-between overflow-hidden">
          <span className="truncate">{label}</span>

          {/* Enterprisey Beta Tag for Investigation+ (hidden when locked
              so the Pro pill takes priority) */}
          {key === "investigations" && !locked && (
            <span className="ml-2 text-[8px] px-1 border border-sky-500/30 text-sky-500/80 rounded uppercase tracking-[0.1em] font-medium leading-3 shrink-0">
              Beta
            </span>
          )}

          {locked && (
            <span className="ml-2 inline-flex items-center gap-0.5 text-[8px] px-1 border border-amber-500/40 text-amber-300 rounded uppercase tracking-[0.1em] font-medium leading-3 shrink-0">
              <Lock className="h-2.5 w-2.5" />
              Pro
            </span>
          )}
        </div>
      )}
    </NavLink>
    );
  })}
</nav>

        {/* Bottom */}
        <div className="mt-auto border-t border-neutral-900 p-2">
          {/* What's new — sits above Help/API Docs because we just shipped
              new features and the announcement is the freshest content here.
              Purple outline frame makes it stand out from the routine
              utility links below. */}
          <button
            onClick={() => {
              openWhatsNew();
              setMobileOpen?.(false);
            }}
            className={`${baseItemClass} border border-purple-500/40 bg-purple-500/[0.06] text-purple-200 hover:bg-purple-500/15 hover:border-purple-500/60 hover:text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]`}
            title="What's new"
          >
            <Megaphone className="h-5 w-5" />
            {!collapsed && (
              <span className="flex flex-1 items-center justify-between overflow-hidden">
                <span className="truncate">What's New</span>
                <span className="ml-2 text-[8px] px-1 border border-purple-400/50 text-purple-200 rounded uppercase tracking-[0.1em] font-bold leading-3 shrink-0">
                  New
                </span>
              </span>
            )}
          </button>

          {NAV_BOTTOM.map(({ key, label, icon: Icon, href, external }) =>
            external ? (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseItemClass} text-neutral-300 hover:bg-neutral-900 hover:text-white`}
                onClick={() => {
                  onChange?.(key);
                  setMobileOpen?.(false);
                }}
              >
                <Icon className="h-5 w-5" />
                {!collapsed && <span className="truncate">{label}</span>}
              </a>
            ) : (
              <NavLink
                key={key}
                to={href}
                className={({ isActive }) =>
                  `${baseItemClass} ${
                    isActive
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-300 hover:bg-neutral-900 hover:text-white"
                  }`
                }
                onClick={() => {
                  onChange?.(key);
                  setMobileOpen?.(false);
                }}
              >
                <Icon className="h-5 w-5" />
                {!collapsed && <span className="truncate">{label}</span>}
              </NavLink>
            )
          )}

          {/* Logout button */}

          <button
            onClick={handleLogout}
            className={`${baseItemClass} text-neutral-300 hover:bg-neutral-900 hover:text-white`}
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span className="truncate">Logout</span>}
          </button>



        </div>
      </aside>

      {/* Spacer to offset content on ALL sizes */}
      <div aria-hidden className={`${widthClass} transition-[width] duration-200`} />
    </>
  );
}
